import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { apiConstant } from 'src/app/utility/constant/api-constant';
import {
    PermissionCode,
    UserRolePermissionMap,
    UserRolePermissionsResponse,
} from 'src/app/model/user-permission.model';

@Injectable({ providedIn: 'root' })
export class PermissionService {
    private readonly apiEndpoint = import.meta.env.NG_APP_API_URL;
    private rolePermissionMap: UserRolePermissionMap | null = null;
    private userPermissions: PermissionCode[] = [];
    private isLocked = false;
    private isRetired = false;

    private readonly _permissions$ = new BehaviorSubject<PermissionCode[]>([]);
    public permissions$ = this._permissions$.asObservable();

    private readonly _currentRole$ = new BehaviorSubject<string>('');
    public currentRole$ = this._currentRole$.asObservable();

    constructor(private readonly http: HttpClient) {}

    fetchUserRolePermissions(): Observable<UserRolePermissionsResponse> {
        return this.http.get<UserRolePermissionsResponse>(
            this.apiEndpoint + apiConstant.fetchUserRolePermissions
        );
    }

    /**
     * Store the full role-permission map from the API response
     * and resolve permissions for the given user role.
     */
    setPermissions(
        rolePermissionMap: UserRolePermissionMap,
        userRole: string
    ): void {
        this.rolePermissionMap = rolePermissionMap;
        const role = userRole?.toLowerCase() ?? '';
        this.userPermissions = rolePermissionMap[role] ?? [];
        this._permissions$.next(this.userPermissions);
        this._currentRole$.next(role);
    }

    /** Get the current role name. */
    getCurrentRole(): string {
        return this._currentRole$.value;
    }

    /** Check if the current user has a specific permission. */
    hasPermission(permission: PermissionCode): boolean {
        return this.userPermissions.includes(permission);
    }

    /** Convenience: user can edit (CanEdit). Returns false if scenario is retired or locked. */
    canEdit(): boolean {
        if (this.isRetired || this.isLocked) {
            return false;
        }
        return this.hasPermission('CanEdit');
    }

    /** Set the locked state */
    setLocked(locked: boolean): void {
        this.isLocked = locked;
    }

    /** Check if scenario is locked */
    getIsLocked(): boolean {
        return this.isLocked;
    }

    /** Set the retired state */
    setRetired(retired: boolean): void {
        this.isRetired = retired;
    }

    /** Check if scenario is retired */
    getIsRetired(): boolean {
        return this.isRetired;
    }

    /** Convenience: user can comment (CanComment). */
    canComment(): boolean {
        if (this.isRetired || this.isLocked) {
            return false;
        }
        return this.hasPermission('CanComment');
    }

    /**
     * Whether the user may resolve or reopen any conversation, not only the
     * ones they started. Owners settle threads on behalf of the team; there is
     * no permission code for it, so the role decides. Read-only while the
     * scenario is retired or locked, like every other write.
     */
    canResolveAnyThread(): boolean {
        if (this.isRetired || this.isLocked) {
            return false;
        }
        return this.getCurrentRole() === 'owner';
    }

    /**
     * Whether the user may reopen any conversation, not only the ones they
     * started: owners and editors (CanEdit). Resolving someone else's thread
     * stays owner-only. Off while the scenario is retired or locked.
     */
    canReopenAnyThread(): boolean {
        if (this.isRetired || this.isLocked) {
            return false;
        }
        return (
            this.getCurrentRole() === 'owner' || this.hasPermission('CanEdit')
        );
    }

    /** Convenience: user can view (CanView). */
    canView(): boolean {
        return this.hasPermission('CanView');
    }

    /** Convenience: user can add owner (CanAddOwner). */
    canAddOwner(): boolean {
        return this.hasPermission('CanAddOwner');
    }

    /** Convenience: user can add user (CanAddUser). */
    canAddUser(): boolean {
        return this.hasPermission('CanAddUser');
    }

    /** Convenience: user can remove owner/user (CanRemoveOwnerUser). */
    canRemoveOwnerUser(): boolean {
        return this.hasPermission('CanRemoveOwnerUser');
    }

    /** Convenience: user can remove user (CanRemoveUser). */
    canRemoveUser(): boolean {
        return this.hasPermission('CanRemoveUser');
    }

    /** Get all current permissions. */
    getPermissions(): PermissionCode[] {
        return [...this.userPermissions];
    }

    /** Get the full role-permission map. */
    getRolePermissionMap(): UserRolePermissionMap | null {
        return this.rolePermissionMap;
    }
}
